import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type ReactNode, type SubmitEvent, useState } from 'react'

import type { Language } from '@/bindings/Language'
import type { QueueOrder } from '@/bindings/QueueOrder'
import { Button } from '@/components/button'
import { buttonClass } from '@/components/button-class'
import { FIELD_LABEL_OFFSET, Field, Input } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { searchOrderQuery, searchSessionsQuery } from '@/features/back-office/api'
import {
  type OrderSearchCriteria,
  type SearchPageSearch,
  type SearchTab,
  type SessionSearchCriteria,
  isOrderNumber,
  normalizeCode,
  orderCriteria,
  sessionCriteria,
} from '@/features/back-office/search-params'
import { SessionCard } from '@/features/back-office/sessions-page'
import { OrderCard } from '@/features/ordering/order-view'
import { cx } from '@/lib/cx'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { todayIn } from '@/lib/time'

const ROUTE_ID = '/$restaurantSlug/$branchSlug/manage/search'
const ROUTE_PATH = '/$restaurantSlug/$branchSlug/manage/search'
const ORDER_PATH = '/$restaurantSlug/$branchSlug/manage/orders/$orderId'

interface Scope {
  restaurantSlug: string
  branchSlug: string
  restaurantId: string
  branchId: string
  /** The restaurant's main language: the T4.3 fallback for item names. */
  main: Language
  /** Today in the branch's zone (T14): the default date of the number and table searches. */
  today: string
  /** Submits: the criteria live in the URL, so a result survives opening it and coming back. */
  go: (next: SearchPageSearch) => void
}

/**
 * Search (PRD, G13): exact lookups for staff helping a customer. Orders by code, or by daily
 * number + date; sessions by code, or by table + date (+ time). Two explicit forms per tab so
 * both ways are visible. Every role in the branch.
 */
export function BranchSearchPage() {
  const t = useT()
  const { restaurantSlug, branchSlug, restaurantId, branchId, context } = useManagedBranch()
  const search = useSearch({ from: ROUTE_ID })
  const navigate = useNavigate()
  const branch = context?.branches.find((candidate) => candidate.id === branchId)

  const scope: Scope = {
    restaurantSlug,
    branchSlug,
    restaurantId,
    branchId,
    main: context.settings.main_language,
    today: todayIn(branch?.timezone),
    go: (next) => void navigate({ to: ROUTE_PATH, params: { restaurantSlug, branchSlug }, search: next }),
  }
  const tabs: { id: SearchTab; label: string }[] = [
    { id: 'orders', label: t('search.tab.orders') },
    { id: 'sessions', label: t('search.tab.sessions') },
  ]

  return (
    <Page width="md" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{t('search.title')}</h1>
        {branch && <span className="text-muted">{branch.name}</span>}
      </div>
      <div role="tablist" aria-label={t('search.tabs')} className="flex gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={search.tab === tab.id}
            onClick={() => scope.go({ tab: tab.id })}
            className={cx(
              'min-h-9 rounded-full border px-3 text-sm font-medium select-none',
              search.tab === tab.id ? 'bg-accent text-accent-fg border-accent' : 'border-border text-muted hover:bg-accent-soft',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" className="flex flex-col gap-4">
        {search.tab === 'orders' ? <OrdersTab scope={scope} search={search} /> : <SessionsTab scope={scope} search={search} />}
      </div>
    </Page>
  )
}

// -- forms --

/** Top-aligned so a hint or error under one field never shifts its neighbors; the button drops to the input's row. */
const ROW = 'flex flex-wrap items-start gap-2'

/** A one-field code form shared by both tabs; the code is validated before it is asked for. */
function CodeForm({ label, code: initial, onSubmit }: { label: string; code: string | undefined; onSubmit: (code: string) => void }) {
  const t = useT()
  const [code, setCode] = useState(initial ?? '')
  const [error, setError] = useState<string>()
  function submit(event: SubmitEvent) {
    event.preventDefault()
    const normalized = normalizeCode(code)
    if (!normalized) {
      setError(t('search.invalidCode'))
      return
    }
    onSubmit(normalized)
  }
  return (
    <form onSubmit={submit} className={ROW}>
      <Field label={label} hint={t('search.codeHint')} error={error} className="min-w-48 grow">
        <Input
          value={code}
          onChange={(event) => {
            setCode(event.target.value)
            setError(undefined)
          }}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          maxLength={10}
        />
      </Field>
      <Button type="submit" className={FIELD_LABEL_OFFSET}>
        {t('search.byCode')}
      </Button>
    </form>
  )
}

function Or() {
  const t = useT()
  return <p className="text-muted text-center text-sm">{t('search.or')}</p>
}

function OrdersTab({ scope, search }: { scope: Scope; search: SearchPageSearch }) {
  const t = useT()
  const [number, setNumber] = useState(search.number ?? '')
  const [date, setDate] = useState(search.date ?? scope.today)
  const [errors, setErrors] = useState<{ number?: string; date?: string }>({})
  const criteria = orderCriteria(search)

  function submitNumber(event: SubmitEvent) {
    event.preventDefault()
    const next = { ...(isOrderNumber(number) ? {} : { number: t('search.invalidNumber') }), ...(date ? {} : { date: t('search.dateRequired') }) }
    setErrors(next)
    if (Object.keys(next).length === 0) scope.go({ tab: 'orders', number: String(Number(number)), date })
  }

  return (
    <>
      <Card className="flex flex-col gap-3">
        <CodeForm label={t('search.orderCode')} code={search.code} onSubmit={(code) => scope.go({ tab: 'orders', code })} />
        <Or />
        <form onSubmit={submitNumber} noValidate className={ROW}>
          <Field label={t('search.number')} error={errors.number} className="w-32">
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={number}
              onChange={(event) => {
                setNumber(event.target.value)
                setErrors({})
              }}
            />
          </Field>
          <Field label={t('search.date')} error={errors.date} className="w-44">
            <Input
              type="date"
              value={date}
              onChange={(event) => {
                setDate(event.target.value)
                setErrors({})
              }}
            />
          </Field>
          <Button type="submit" className={FIELD_LABEL_OFFSET}>
            {t('search.byNumber')}
          </Button>
        </form>
      </Card>
      {criteria && <OrderResult scope={scope} criteria={criteria} />}
    </>
  )
}

function SessionsTab({ scope, search }: { scope: Scope; search: SearchPageSearch }) {
  const t = useT()
  const [table, setTable] = useState(search.table ?? '')
  const [date, setDate] = useState(search.date ?? scope.today)
  const [time, setTime] = useState(search.time ?? '')
  const [errors, setErrors] = useState<{ table?: string; date?: string }>({})
  const criteria = sessionCriteria(search)

  function submitTable(event: SubmitEvent) {
    event.preventDefault()
    const name = table.trim()
    const next = { ...(name ? {} : { table: t('search.tableRequired') }), ...(date ? {} : { date: t('search.dateRequired') }) }
    setErrors(next)
    if (Object.keys(next).length === 0) scope.go({ tab: 'sessions', table: name, date, ...(time ? { time } : {}) })
  }

  return (
    <>
      <Card className="flex flex-col gap-3">
        <CodeForm label={t('search.sessionCode')} code={search.code} onSubmit={(code) => scope.go({ tab: 'sessions', code })} />
        <Or />
        <form onSubmit={submitTable} className={ROW}>
          <Field label={t('search.table')} hint={t('search.tableHint')} error={errors.table} className="min-w-40 grow">
            {/* Typed, not picked: matched on the session's snapshotted name, so renamed and deleted tables keep their history. */}
            <Input
              value={table}
              autoComplete="off"
              onChange={(event) => {
                setTable(event.target.value)
                setErrors({})
              }}
            />
          </Field>
          <Field label={t('search.date')} error={errors.date} className="w-44">
            <Input
              type="date"
              value={date}
              onChange={(event) => {
                setDate(event.target.value)
                setErrors({})
              }}
            />
          </Field>
          <Field label={t('search.time')} hint={t('common.optional')} className="w-32">
            <Input type="time" value={time} onChange={(event) => setTime(event.target.value)} />
          </Field>
          <Button type="submit" className={FIELD_LABEL_OFFSET}>
            {t('search.byTable')}
          </Button>
        </form>
      </Card>
      {criteria && <SessionResults scope={scope} criteria={criteria} />}
    </>
  )
}

// -- results --

function Result({ query, children }: { query: { isPending: boolean; data: unknown; error: unknown; refetch: () => unknown }; children: ReactNode }) {
  const t = useT()
  if (query.isPending) return <p className="text-muted text-sm">{t('common.loading')}</p>
  if (!query.data) {
    return (
      <div className="flex flex-col gap-2">
        <FormError error={query.error} />
        <Button variant="secondary" onClick={() => void query.refetch()} className="self-start">
          {t('common.retry')}
        </Button>
      </div>
    )
  }
  return children
}

function OrderResult({ scope, criteria }: { scope: Scope; criteria: OrderSearchCriteria }) {
  const t = useT()
  const query = useQuery(searchOrderQuery(scope.restaurantId, scope.branchId, criteria))
  return (
    <Result query={query}>
      {query.data?.order ? (
        <OrderHit scope={scope} order={query.data.order} />
      ) : (
        <p role="status" className="text-muted text-sm">
          {t('search.noOrder')}
        </p>
      )}
    </Result>
  )
}

/** The queue's card without its moves: Details opens the page that has them. */
function OrderHit({ scope, order }: { scope: Scope; order: QueueOrder }) {
  const t = useT()
  const params = { restaurantSlug: scope.restaurantSlug, branchSlug: scope.branchSlug, orderId: order.id }
  return (
    <OrderCard
      order={order}
      currency={order.currency}
      main={scope.main}
      code
      title={
        <Link to={ORDER_PATH} params={params} className="hover:underline">
          {t('track.title', { number: order.number })}
        </Link>
      }
      actions={
        <div className="flex justify-end">
          <Link to={ORDER_PATH} params={params} className={buttonClass('primary', 'sm')}>
            {t('queue.details')}
          </Link>
        </div>
      }
    />
  )
}

function SessionResults({ scope, criteria }: { scope: Scope; criteria: SessionSearchCriteria }) {
  const t = useT()
  const query = useQuery(searchSessionsQuery(scope.restaurantId, scope.branchId, criteria))
  return (
    <Result query={query}>
      {query.data && query.data.sessions.length === 0 ? (
        <p role="status" className="text-muted text-sm">
          {t('search.noSessions')}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {query.data?.sessions.map((row) => (
            <li key={row.session.id}>
              <SessionCard row={row} currency={query.data.currency} />
            </li>
          ))}
        </ul>
      )}
    </Result>
  )
}
